import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  getInvitationCapacity,
  getOrganizations,
  getWorkspaceCapabilities,
  getWorkspaces,
  tenantKeys,
} from '../features/tenancy/tenancyApi';
import { ACTIONS } from '../features/tenancy/actions';

// Query keys for workspace-scoped resources, dropped when switching workspaces.
const WORKSPACE_SCOPED_KEYS = ['folder-contents', 'trash-files', 'trash-folders', 'shares'];

/**
 * Resolves the caller's active organization + workspace after login and keeps
 * the selection in the auth store (which the API layer reads to scope requests).
 * `ready` gates rendering of workspace-scoped pages until a valid workspace is
 * selected, so the API client never fires without one.
 */
export function useTenant() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken));
  const storedOrgId = useAuthStore((s) => s.orgId);
  const storedWorkspaceId = useAuthStore((s) => s.workspaceId);
  const setTenant = useAuthStore((s) => s.setTenant);

  const orgsQuery = useQuery({
    queryKey: tenantKeys.organizations(),
    queryFn: getOrganizations,
    enabled: isAuthenticated,
  });
  const orgs = orgsQuery.data ?? [];
  const activeOrgId = orgs.find((o) => o.id === storedOrgId)?.id ?? orgs[0]?.id ?? null;
  const organization = orgs.find((o) => o.id === activeOrgId) ?? null;

  const workspacesQuery = useQuery({
    queryKey: tenantKeys.workspaces(activeOrgId),
    queryFn: () => getWorkspaces(activeOrgId),
    enabled: isAuthenticated && Boolean(activeOrgId),
  });
  const workspaces = workspacesQuery.data ?? [];
  const workspace =
    workspaces.find((w) => w.id === storedWorkspaceId) ??
    workspaces.find((w) => w.type === 'personal') ??
    workspaces[0] ??
    null;

  const capabilitiesQuery = useQuery({
    queryKey: ['workspace-capabilities', activeOrgId, workspace?.id],
    queryFn: () => getWorkspaceCapabilities({ orgId: activeOrgId, workspaceId: workspace.id }),
    enabled: isAuthenticated && Boolean(activeOrgId && workspace?.id),
  });
  const actions = new Set(capabilitiesQuery.data?.actions ?? []);
  const allows = (action) => actions.has(action);
  const readOnly = Boolean(capabilitiesQuery.data?.readOnly);
  // A read-only organization keeps its permissions (delete, rename, move,
  // restore) but cannot add anything new; the server enforces the same rule.
  const allowsAdding = (action) => allows(action) && !readOnly;
  const mayManageInvites = allows(ACTIONS.MEMBER_INVITE);
  const inviteCapacityQuery = useQuery({
    queryKey: ['invitation-capacity', activeOrgId],
    queryFn: () => getInvitationCapacity(activeOrgId),
    enabled: isAuthenticated && Boolean(activeOrgId) && mayManageInvites,
  });

  // Persist the resolved selection so the API layer can read it.
  useEffect(() => {
    if (activeOrgId && workspace && (storedOrgId !== activeOrgId || storedWorkspaceId !== workspace.id)) {
      setTenant({ orgId: activeOrgId, workspaceId: workspace.id });
    }
  }, [activeOrgId, workspace, storedOrgId, storedWorkspaceId, setTenant]);

  const ready =
    Boolean(activeOrgId) &&
    workspaces.some((w) => w.id === storedWorkspaceId) &&
    capabilitiesQuery.isSuccess;

  // The caller's org role comes back on each org (GET /organizations). Derive the
  // handful of UI capability flags from it; backend guards remain the source of
  // truth, so this only governs whether a control is shown.
  const role = organization?.role ?? null;
  const isAdmin = role === 'admin';
  const can = {
    manageOrg: isAdmin,
    manageMembers: isAdmin && allows(ACTIONS.MEMBER_MANAGE),
    // Admins invite/create workspaces (per-workspace mutations additionally
    // require owning that workspace). Members have content permissions only.
    manageInvites: isAdmin && mayManageInvites,
    invite: isAdmin && mayManageInvites && inviteCapacityQuery.data?.canInvite === true,
    createWorkspace: isAdmin,
    transferOwnership: isAdmin,
    // Mirrors backend SIGNATURE_SEND (admin-only now); guards remain the truth.
    sendSignatures: allows(ACTIONS.SIGNATURE_SEND),
    manageForms: allows(ACTIONS.FORM_MANAGE),
  };

  const switchWorkspace = (workspaceId) => {
    if (workspaceId === storedWorkspaceId) return;
    setTenant({ orgId: activeOrgId, workspaceId });
    // Drop caches scoped to the previous workspace so the new one loads fresh.
    WORKSPACE_SCOPED_KEYS.forEach((key) => queryClient.removeQueries({ queryKey: [key] }));
    navigate('/files');
  };

  // Switch organization. The workspace is deliberately cleared: `ready` stays
  // false (spinner, no workspace-scoped calls) until the effect above resolves
  // the new org's personal/first workspace and persists it.
  const switchOrg = (orgId) => {
    if (orgId === activeOrgId) return;
    setTenant({ orgId, workspaceId: null });
    WORKSPACE_SCOPED_KEYS.forEach((key) => queryClient.removeQueries({ queryKey: [key] }));
    navigate('/files');
  };

  return {
    ready,
    isLoading: orgsQuery.isLoading || workspacesQuery.isLoading,
    error: orgsQuery.error ?? workspacesQuery.error,
    orgs,
    organization,
    activeOrgId,
    role,
    can,
    actions,
    allows,
    allowsAdding,
    /** The organization has no plan in force: view and download only. */
    readOnly,
    inviteCapacity: inviteCapacityQuery.data ?? null,
    workspaces,
    workspace,
    switchWorkspace,
    switchOrg,
  };
}
